import { useEffect, useRef, useCallback } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import type { StreamChunk } from '../shared/types'

export interface TerminalHandle {
  writeChunk: (chunk: StreamChunk) => void
  writeIteration: (iteration: number, maxIterations: number) => void
  clear: () => void
}

// Singleton-ish ref so other components can write to the terminal
let globalTerminalHandle: TerminalHandle | null = null
export function getTerminalHandle(): TerminalHandle | null {
  return globalTerminalHandle
}

export function OutputTerminal() {
  const containerRef = useRef<HTMLDivElement>(null)
  const termRef = useRef<Terminal | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const terminal = new Terminal({
      theme: {
        background: '#06060a',
        foreground: '#d4d4d8',
        cursor: '#00ff88',
        cursorAccent: '#06060a',
        selectionBackground: '#00ff8822',
        selectionForeground: '#fafafa',
        black: '#06060a',
        red: '#ef4444',
        green: '#00ff88',
        yellow: '#eab308',
        blue: '#5b8def',
        magenta: '#a78bfa',
        cyan: '#67e8f9',
        white: '#d4d4d8',
        brightBlack: '#3f3f46',
        brightRed: '#f87171',
        brightGreen: '#34d399',
        brightYellow: '#fbbf24',
        brightBlue: '#93c5fd',
        brightMagenta: '#c4b5fd',
        brightCyan: '#a5f3fc',
        brightWhite: '#fafafa'
      },
      fontFamily: "'JetBrains Mono', 'Cascadia Code', Consolas, monospace",
      fontSize: 13,
      lineHeight: 1.4,
      scrollback: 10000,
      convertEol: true,
      cursorBlink: true,
      cursorStyle: 'bar',
      cursorWidth: 2,
      disableStdin: true,
      letterSpacing: 0
    })

    const fitAddon = new FitAddon()
    terminal.loadAddon(fitAddon)
    terminal.open(containerRef.current)

    requestAnimationFrame(() => {
      try { fitAddon.fit() } catch {}
    })

    termRef.current = terminal

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        try { fitAddon.fit() } catch {}
      })
    })
    resizeObserver.observe(containerRef.current)

    // Expose handle for other components
    globalTerminalHandle = {
      writeChunk(chunk: StreamChunk) {
        switch (chunk.type) {
          case 'text':
            terminal.write(chunk.content)
            break
          case 'tool-use':
            terminal.write(`\r\n\x1b[36;1m${chunk.content}\x1b[0m\r\n`)
            break
          case 'tool-result':
            terminal.write(`\x1b[38;5;240m${chunk.content}\x1b[0m\r\n`)
            break
          case 'error':
            terminal.write(`\x1b[31;1m${chunk.content}\x1b[0m\r\n`)
            break
          case 'status':
            terminal.write(`\x1b[33m${chunk.content}\x1b[0m\r\n`)
            break
        }
      },
      writeIteration(iteration: number, maxIterations: number) {
        const line = '\u2550'.repeat(50)
        terminal.write(`\r\n\x1b[32;1m${line}\x1b[0m\r\n`)
        terminal.write(`\x1b[32;1m  ITERATION ${iteration} / ${maxIterations}\x1b[0m\r\n`)
        terminal.write(`\x1b[32;1m${line}\x1b[0m\r\n\r\n`)
      },
      clear() {
        terminal.clear()
      }
    }

    return () => {
      globalTerminalHandle = null
      resizeObserver.disconnect()
      terminal.dispose()
    }
  }, [])

  const handleClear = useCallback(() => {
    termRef.current?.clear()
  }, [])

  return (
    <div className="terminal-container">
      <div className="terminal-header">
        <span>Output</span>
        <button className="btn-small" onClick={handleClear}>Clear</button>
      </div>
      <div className="terminal-body" ref={containerRef} />
    </div>
  )
}
